import { MenuOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Alert, Badge, Button, Drawer, Grid, Layout, Menu, Space, Spin, Tag, Typography } from "antd";
import type { MenuProps } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { apiFetch } from "../api/client";
import type { SidebarResponse, SidebarSection } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { UserMenu } from "./UserMenu";

const { Header, Sider, Content } = Layout;

/**
 * The navigation is data driven: MainController#sidebar already resolves which
 * sections and entries the user's roles allow, including the badge counts. The
 * client only renders what it is given and must not re-derive permissions.
 */
function toMenuItems(sections: SidebarSection[]): MenuProps["items"] {
  return sections.map((section) => ({
    key: section.id,
    label:
      section.badge > 0 ? (
        <Space>
          {section.label}
          <Badge count={section.badge} size="small" />
        </Space>
      ) : (
        section.label
      ),
    children: section.items
      .filter((item) => item.type === "item")
      .map((item) => ({
        // The route is derived from the controller/action pair the API reports,
        // which keeps the URL space aligned with the old application.
        key: `/${item.controller ?? ""}/${item.action ?? ""}`,
        label:
          (item.badge ?? 0) > 0 ? (
            <Space>
              {item.label}
              <Badge count={item.badge} size="small" />
            </Space>
          ) : (
            item.label
          ),
      })),
  }));
}

export function AppLayout() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isPending, error } = useQuery({
    queryKey: ["sidebar"],
    queryFn: () => apiFetch<SidebarResponse>("/main/sidebar"),
  });

  const sections = useMemo(() => data?.data.sections ?? [], [data]);
  const items = useMemo(() => toMenuItems(sections), [sections]);
  const loginAs = data?.data.login_as;

  // Which block a screen belongs to, keyed the same way the menu entries are.
  const sectionOfPath = useMemo(() => {
    const owners = new Map<string, string>();

    for (const section of sections) {
      for (const item of section.items) {
        if (item.type !== "item") continue;
        owners.set(`/${item.controller ?? ""}/${item.action ?? ""}`, section.id);
      }
    }

    return owners;
  }, [sections]);

  // The user's own block. MainController#navigation_sections always puts it
  // first and labels it with the name of the account — it is where a session
  // starts, and where screens the sidebar does not list (the account, the
  // privacy policy) belong.
  const ownSection = sections[0]?.id;
  const activeSection = sectionOfPath.get(location.pathname);

  // One block at a time, and it is the one being worked in. Everything used to
  // stand open, which on an account with several roles is a wall of entries
  // where the four that matter right now are somewhere in the middle.
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    const key = activeSection ?? ownSection;
    setOpenKeys(key ? [key] : []);
  }, [activeSection, ownSection]);

  // Unterhalb von lg (992 px) wird die Seitenleiste zur Schublade. Frueher klappte
  // sie dort auf Breite 0 zusammen und liess sich nur ueber einen kleinen Zipfel
  // am Rand wieder oeffnen, den auf einem Telefon niemand findet.
  const screens = Grid.useBreakpoint();
  const isDesktop = screens.lg === true;
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Wer in der Schublade einen Bildschirm waehlt, will ihn sehen und nicht die
  // Schublade, die ihn verdeckt.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // The sidebar lists what the roles allow and has no entry for the landing
  // screen — the old application reached it through the "home" tab, which this
  // shell does not have. The title is the way back.
  const navigation = (
    <>
      <div style={{ padding: 16 }}>
        <Typography.Title
          level={4}
          style={{ margin: 0, cursor: "pointer" }}
          onClick={() => navigate("/")}
        >
          myspeechplan
        </Typography.Title>
      </div>

      {isPending && (
        <div style={{ padding: 16, textAlign: "center" }}>
          <Spin />
        </div>
      )}

      {error && (
        <Alert
          type="error"
          style={{ margin: 16 }}
          title={error instanceof Error ? error.message : String(error)}
        />
      )}

      {!isPending && !error && (
        <Menu
          mode="inline"
          items={items}
          openKeys={openKeys}
          // Opening one block closes the other: antd reports the whole set,
          // so the one that was just opened is the one we did not have.
          // Closing the last one leaves the menu collapsed, which is a
          // deliberate choice of the reader and not overruled here.
          onOpenChange={(keys) => {
            const opened = keys.find((key) => !openKeys.includes(key));
            setOpenKeys(opened ? [opened] : []);
          }}
          selectedKeys={[location.pathname]}
          onClick={({ key }) => navigate(key)}
        />
      )}
    </>
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {isDesktop ? (
        <Sider width={260} theme="light">
          {navigation}
        </Sider>
      ) : (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          size={280}
          closable={false}
          styles={{ body: { padding: 0 } }}
        >
          {navigation}
        </Drawer>
      )}

      <Layout>
        <Header
          style={{
            background: "#fff",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            paddingInline: isDesktop ? 24 : 8,
          }}
        >
          {/* Auf dem Telefon: Schublade und Titel. Auf dem Desktop steht der
              Titel schon in der Seitenleiste, die Kopfzeile bleibt dort links
              leer. */}
          <Space size={4}>
            {!isDesktop && (
              <>
                <Button
                  type="text"
                  icon={<MenuOutlined />}
                  aria-label={t("navigation.title")}
                  onClick={() => setDrawerOpen(true)}
                />
                <Typography.Text strong style={{ cursor: "pointer" }} onClick={() => navigate("/")}>
                  myspeechplan
                </Typography.Text>
              </>
            )}
          </Space>

          <Space size={4} style={{ minWidth: 0 }}>
            {/* Support access. Everything below this header then shows what the
                other account sees, so the one place that can say whose data
                this is, is the name. The old application appended the same in
                brackets (User#username_for_login_as_access). */}
            {loginAs && (
              <Tag
                color="orange"
                style={{
                  marginInlineEnd: 0,
                  maxWidth: isDesktop ? undefined : 140,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={`${t("user.logis_as_access")} ${loginAs.name}`}
              >
                {t("user.logis_as_access")} {loginAs.name}
              </Tag>
            )}
            {user && <UserMenu compact={!isDesktop} />}
          </Space>
        </Header>

        <Content style={{ padding: isDesktop ? 24 : 12 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
